import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ReceivablesService from '../../Services/receivables.service';

/**
 * Fetches the Accounts Receivable page payload (invoices, per-card aggregation, totals)
 * for a single client (caseId), with optional date range / card / status filters.
 */
export const fetchReceivablesPageData = createAsyncThunk(
    'receivablesPage/fetchData',
    async ({ caseId, token, dateFrom, dateTo, cardId, status }, { rejectWithValue }) => {
        try {
            const res = await ReceivablesService.GetReceivablesPage(caseId, token, {
                dateFrom,
                dateTo,
                cardId,
                status,
            });
            if (!res.ok) {
                const text = await res.text();
                return rejectWithValue(text || 'Failed to load receivables');
            }
            const data = await res.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const emptyTotals = {
    totalInvoiced: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    expected_0_30: 0,
    expected_30_60: 0,
    expected_60_90: 0,
    expected_Over_90: 0,
    overdueAmount: 0,
    missingReceiptCount: 0,
};

const initialState = {
    invoices: [],
    cards: [],
    unlinkedReceipts: [],
    totals: emptyTotals,
    loading: false,
    error: null,
};

const receivablesPageSlice = createSlice({
    name: 'receivablesPage',
    initialState,
    reducers: {
        resetReceivablesPage: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchReceivablesPageData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReceivablesPageData.fulfilled, (state, action) => {
                const payload = action.payload || {};
                state.invoices = Array.isArray(payload.invoices) ? payload.invoices : [];
                state.cards = Array.isArray(payload.cards) ? payload.cards : [];
                state.unlinkedReceipts = Array.isArray(payload.unlinkedReceipts) ? payload.unlinkedReceipts : [];
                state.totals = payload.totals
                    ? {
                          totalInvoiced: payload.totals.totalInvoiced ?? 0,
                          totalPaid: payload.totals.totalPaid ?? 0,
                          totalOutstanding: payload.totals.totalOutstanding ?? 0,
                          expected_0_30: payload.totals.expected_0_30 ?? 0,
                          expected_30_60: payload.totals.expected_30_60 ?? 0,
                          expected_60_90: payload.totals.expected_60_90 ?? 0,
                          expected_Over_90: payload.totals.expected_Over_90 ?? 0,
                          overdueAmount: payload.totals.overdueAmount ?? 0,
                          missingReceiptCount: payload.totals.missingReceiptCount ?? 0,
                      }
                    : emptyTotals;
                state.loading = false;
            })
            .addCase(fetchReceivablesPageData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetReceivablesPage } = receivablesPageSlice.actions;
export default receivablesPageSlice.reducer;
